import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Plus } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import { fuelService } from '../../services/fuel.service';
import { FuelRecord } from '../../types';
import { RecordFuelModal } from './RecordFuelModal';

export const FuelRecordsPage: React.FC = () => {
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [isRecordModalOpen, setIsRecordModalOpen] = useState(false);

  const { data: allRecords, isLoading: isLoadingAll, error: allError } = useQuery({
    queryKey: ['fuel'],
    queryFn: fuelService.getAll,
    enabled: !showFlaggedOnly,
  });

  const { data: flaggedRecords, isLoading: isLoadingFlagged, error: flaggedError } = useQuery({
    queryKey: ['fuel', 'flagged'],
    queryFn: fuelService.getFlagged,
    enabled: showFlaggedOnly,
  });

  const records = useMemo<FuelRecord[]>(() => {
    return (showFlaggedOnly ? flaggedRecords : allRecords) || [];
  }, [allRecords, flaggedRecords, showFlaggedOnly]);

  const isLoading = showFlaggedOnly ? isLoadingFlagged : isLoadingAll;
  const error = showFlaggedOnly ? flaggedError : allError;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
  };

  return (
    <div className="space-y-6">
      <Card
        title="Fuel Records"
        action={
          <div className="flex items-center gap-2">
            <Button
              variant={showFlaggedOnly ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setShowFlaggedOnly(false)}
            >
              All
            </Button>
            <Button
              variant={showFlaggedOnly ? 'ghost' : 'secondary'}
              size="sm"
              onClick={() => setShowFlaggedOnly(true)}
            >
              Flagged
            </Button>
            <Button size="sm" onClick={() => setIsRecordModalOpen(true)}>
              <Plus size={16} className="mr-2" />
              Record Fuel
            </Button>
          </div>
        }
      >
        {isLoading && <div className="text-sm text-gray-600">Loading...</div>}
        {!isLoading && error && (
          <div className="text-sm text-error">Failed to load fuel records.</div>
        )}

        {!isLoading && !error && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Trip</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Cost/L</TableHead>
                  <TableHead>Total Cost</TableHead>
                  <TableHead>Expected</TableHead>
                  <TableHead>Variance</TableHead>
                  <TableHead>Variance %</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.length === 0 ? (
                  <TableRow>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" colSpan={12}>
                      No fuel records found.
                    </td>
                  </TableRow>
                ) : (
                  records.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>
                        {r.fuelDate ? format(parseISO(r.fuelDate), 'MMM dd, yyyy') : '-'}
                      </TableCell>
                      <TableCell className="font-medium">{r.tripNumber}</TableCell>
                      <TableCell>{r.vehicleRegistration}</TableCell>
                      <TableCell>
                        <Badge variant={r.transactionType === 'PURCHASED' ? 'success' : 'info'}>
                          {r.transactionType}
                        </Badge>
                      </TableCell>
                      <TableCell>{r.quantityLiters.toFixed(2)} L</TableCell>
                      <TableCell>{formatCurrency(r.costPerLiter)}</TableCell>
                      <TableCell>{formatCurrency(r.totalCost)}</TableCell>
                      <TableCell>
                        {r.expectedConsumptionLiters ? `${r.expectedConsumptionLiters.toFixed(2)} L` : '-'}
                      </TableCell>
                      <TableCell>
                        {r.varianceLiters !== undefined && r.varianceLiters !== null ? (
                          <span className={r.varianceLiters > 0 ? 'text-red-600' : 'text-green-600'}>
                            {r.varianceLiters > 0 ? '+' : ''}{r.varianceLiters.toFixed(2)} L
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {typeof r.variancePercentage === 'number' ? (
                          <span className={r.variancePercentage > 5 ? 'text-red-600 font-semibold' : ''}>
                            {r.variancePercentage > 0 ? '+' : ''}{r.variancePercentage.toFixed(2)}%
                          </span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {r.flaggedForReview ? (
                          <Badge variant="error">Flagged</Badge>
                        ) : (
                          <Badge variant="default">OK</Badge>
                        )}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">{r.notes || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <RecordFuelModal isOpen={isRecordModalOpen} onClose={() => setIsRecordModalOpen(false)} />
    </div>
  );
};
